import { TaskTemplateAssigneeRole } from "@common/types/taskTemplate";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CloseIcon } from "@icons";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { Separator } from "@design-system/Separator/Separator";
import { HStack } from "@design-system/HStack/HStack";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { Tooltip } from "@design-system/Tooltip/Tooltip";

interface Props {
    selectedBulkIds: string[];
    totalFilteredEntityCount: number;
    activeRoles: Set<TaskTemplateAssigneeRole>;
    hasSelectedEntities: boolean;
    hasAllSelectedBulkEntitiesSelected: boolean;
    onSelectAll: () => void;
    onUnselectSelected: () => void;
    onSelectForApplication: () => void;
    onDeselectForApplication: () => void;
    onOpenBulkPreparer: () => void;
    onOpenBulkReviewer: () => void;
    onOpenBulkManager: () => void;
    onOpenBulkActions: () => void;
    onBack: () => void;
    onContinue: () => void;
}

export const ApplyCloseTemplateEntitySelectionFooter = ({
    selectedBulkIds,
    totalFilteredEntityCount,
    activeRoles,
    hasSelectedEntities,
    hasAllSelectedBulkEntitiesSelected,
    onSelectAll,
    onUnselectSelected,
    onSelectForApplication,
    onDeselectForApplication,
    onOpenBulkPreparer,
    onOpenBulkReviewer,
    onOpenBulkManager,
    onOpenBulkActions,
    onBack,
    onContinue,
}: Props) => (
    <CommandIsland
        sticky
        alignEnd
        className={commandIslandStyles.bulkActionsStickyFooter}
    >
        <HStack gap={8} className="mr-auto">
            <Button
                variant="primary"
                size="md"
                type="button"
                onClick={onSelectAll}
                disabled={
                    totalFilteredEntityCount === 0 ||
                    selectedBulkIds.length === totalFilteredEntityCount
                }
            >
                Select all
            </Button>
            {selectedBulkIds.length > 0 && (
                <Button
                    variant="primary"
                    size="md"
                    rightIcon={<CloseIcon />}
                    type="button"
                    onClick={onUnselectSelected}
                >
                    {selectedBulkIds.length} selected
                </Button>
            )}
        </HStack>
        {selectedBulkIds.length > 0 && (
            <HStack gap={8}>
                <Button
                    variant="primary"
                    size="md"
                    type="button"
                    onClick={
                        hasAllSelectedBulkEntitiesSelected
                            ? onDeselectForApplication
                            : onSelectForApplication
                    }
                >
                    {hasAllSelectedBulkEntitiesSelected
                        ? "Deselect for application"
                        : "Select for application"}
                </Button>
                <Separator orientation="vertical" />
                {activeRoles.has(TaskTemplateAssigneeRole.PREPARER) && (
                    <WithHotkey
                        hotKey="p"
                        hotKeyOptions={{
                            description: "Set preparer",
                            preventDefault: true,
                        }}
                        tooltip="Set preparer"
                    >
                        <Button
                            variant="primary"
                            size="md"
                            type="button"
                            onClick={onOpenBulkPreparer}
                        >
                            Set preparer
                        </Button>
                    </WithHotkey>
                )}
                {activeRoles.has(TaskTemplateAssigneeRole.REVIEWER) && (
                    <WithHotkey
                        hotKey="r"
                        hotKeyOptions={{
                            description: "Set reviewer",
                            preventDefault: true,
                        }}
                        tooltip="Set reviewer"
                    >
                        <Button
                            variant="primary"
                            size="md"
                            type="button"
                            onClick={onOpenBulkReviewer}
                        >
                            Set reviewer
                        </Button>
                    </WithHotkey>
                )}
                {activeRoles.has(TaskTemplateAssigneeRole.MANAGER) && (
                    <WithHotkey
                        hotKey="m"
                        hotKeyOptions={{
                            description: "Set manager",
                            preventDefault: true,
                        }}
                        tooltip="Set manager"
                    >
                        <Button
                            variant="primary"
                            size="md"
                            type="button"
                            onClick={onOpenBulkManager}
                        >
                            Set manager
                        </Button>
                    </WithHotkey>
                )}
                <Tooltip content="Open bulk actions">
                    <Button
                        variant="primary"
                        size="md"
                        rightIcon={
                            <span data-slot-box="">
                                <Shortcut keys="mod+k" />
                            </span>
                        }
                        type="button"
                        onClick={onOpenBulkActions}
                    >
                        Actions
                    </Button>
                </Tooltip>
                <Separator orientation="vertical" />
            </HStack>
        )}
        <ButtonWithLoader
            variant="secondary"
            size="md"
            type="button"
            loading={false}
            onClick={onBack}
        >
            Back
        </ButtonWithLoader>
        <ButtonWithLoader
            variant="primary"
            size="md"
            type="button"
            loading={false}
            onClick={onContinue}
            disabled={!hasSelectedEntities}
            hotKey="mod+enter"
            hotKeyOptions={{
                description: "Preview selection",
                preventDefault: true,
            }}
            hotKeyDisplayPosition="inline"
        >
            Preview selection
        </ButtonWithLoader>
    </CommandIsland>
);
